import React from 'react';
import { FaWhatsapp, FaTruck, FaCheckCircle, FaClock } from 'react-icons/fa';
import { FiPhone, FiCopy, FiPackage } from 'react-icons/fi';
import { fetchAPI } from '../../../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

export default function BankDepositInfo({ bankDetails }) {
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <div className="space-y-6">
            {/* Simple Blue Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 text-white shadow-lg shadow-blue-200">
                <h1 className="text-3xl font-bold mb-1 font-display">Bank Deposit Information</h1>
                <p className="text-blue-100 opacity-90 font-display">Direct bank or ATM deposits for class payments</p>
            </div>

            {/* Instruction Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-emerald-600 rounded-lg p-6 text-white flex items-center gap-5 shadow-lg shadow-emerald-200 lg:hover:scale-[1.02] transition-transform font-display"
                >
                    <div className="bg-white/20 p-3 rounded-lg">
                        <FaWhatsapp size={28} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-0.5">WhatsApp Support</p>
                        <p className="text-xl font-bold mb-1"> <a href="https://wa.me/94714336294" target="_blank" className="hover:underline">071 433 6294</a></p>
                        <p className="text-[10px] leading-tight opacity-90">අංකය සහ පන්තිය සදහන් කර whatsapp කරන්න.</p>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white border border-gray-100 rounded-lg p-6 flex items-center gap-5 shadow-sm lg:hover:scale-[1.02] transition-transform font-display"
                >
                    <div className="bg-blue-50 text-blue-600 p-3 rounded-lg">
                        <FiPhone size={28} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Technical Support</p>
                        <p className="text-xl font-bold text-gray-900"> <a href="https://wa.me/94719304566" target="_blank" className="hover:text-blue-600 transition-colors">076 85 82 057</a></p>
                        <p className="text-[10px] text-gray-500">Available Daily: 8 AM - 4 PM</p>
                    </div>
                </motion.div>
            </div>

            {/* Bank Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {bankDetails.map((bank, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 + (index * 0.1) }}
                        className="bg-white border border-gray-100 rounded-lg p-6 hover:shadow-xl hover:shadow-gray-200/50 transition-all group font-display"
                    >
                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-16 h-12 flex-shrink-0 flex items-center justify-center p-2 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                                <img src={bank.logo} alt={bank.bank} className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-blue-600 font-bold text-sm truncate uppercase tracking-tight">{bank.bank}</h3>
                                <p className="text-[13px] text-gray-400 font-medium">Branch: {bank.branch}</p>
                            </div>
                        </div>

                        <div className="space-y-3 pt-3 border-t border-gray-50">
                            <div>
                                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Account Name</label>
                                <p className="text-s font-bold text-gray-800 uppercase">{bank.accountName}</p>
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Account Number</label>
                                <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg group-hover:bg-blue-50/50 transition-colors">
                                    <span className="text-sm font-mono font-bold text-gray-900">{bank.accountNo}</span>
                                    <button
                                        onClick={() => copyToClipboard(bank.accountNo)}
                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-white rounded-md transition-all shadow-sm"
                                        title="Copy"
                                    >
                                        <FiCopy size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* My Tute Deliveries Section */}
            <MyDeliveries />
        </div>
    );
}

function MyDeliveries() {
    const [deliveries, setDeliveries] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const loadDeliveries = async () => {
            try {
                const data = await fetchAPI('/student/get_deliveries.php');
                if (Array.isArray(data)) setDeliveries(data);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        loadDeliveries();
    }, []);

    if (loading) return (
        <div className="bg-white rounded-lg p-8 border border-gray-100 flex flex-col items-center justify-center gap-3 animate-pulse">
            <div className="w-10 h-10 bg-gray-100 rounded-full"></div>
            <div className="h-4 w-32 bg-gray-100 rounded-full"></div>
        </div>
    );

    if (deliveries.length === 0) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm overflow-hidden relative font-display"
        >
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 opacity-50 pointer-events-none"></div>

            <div className="flex items-center gap-3 mb-6 relative z-10">
                <div className="bg-blue-600 text-white p-2.5 rounded-xl shadow-lg shadow-blue-200">
                    <FaTruck size={18} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-gray-900">My Tute Deliveries</h2>
                    <p className="text-xs text-gray-400">Track your study packs</p>
                </div>
            </div>

            <div className="space-y-3 relative z-10">
                <AnimatePresence>
                    {deliveries.map((pkg, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="group flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 hover:shadow-md transition-all duration-300"
                        >
                            <div className="flex items-center gap-4 mb-3 sm:mb-0">
                                <div className={`p-3 rounded-full ${pkg.status === 'Sent' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                                    <FiPackage size={20} />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-gray-900">{pkg.month} Tute Pack</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <FaClock className="text-gray-300 text-[10px]" />
                                        <p className="text-xs text-gray-500">{new Date(pkg.created_at).toLocaleDateString()}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full sm:w-auto text-left sm:text-right pl-[52px] sm:pl-0">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide ${pkg.status === 'Sent' ? 'bg-emerald-100 text-emerald-700' :
                                    pkg.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                    {pkg.status === 'Sent' && <FaCheckCircle size={10} />}
                                    {pkg.status}
                                </span>
                                {pkg.tracking_no && (
                                    <div className="mt-2 bg-white/50 px-3 py-1.5 rounded-lg border border-gray-200/50 inline-block">
                                        <p className="text-[10px] font-mono text-gray-500 flex items-center gap-1.5">
                                            <span className="font-bold text-gray-400">TRK:</span>
                                            <span className="text-blue-600 font-bold">{pkg.tracking_no}</span>
                                            <button onClick={() => navigator.clipboard.writeText(pkg.tracking_no)} className="hover:text-blue-800"><FiCopy size={10} /></button>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}
